import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet/es/Helmet'
// Component
import { EmptyText, TextWrap } from '../../../components/Common/CellText'
import { StatusTag } from '../../../components/Common/StatusTag'
import TableComponent from '../../../components/Common/TableComponent'
import PageTitle from '../../../components/PageTitle'
import UnreadText from '../../../components/UnreadText/UnreadText'
import AdvanceFilterBlock from './AdvanceFilterBlock'
// Layout
import DocumentsLayout from '../../../layouts/DocumentsLayout'
// styled Component
import {
  FilterButtonWrapper,
  SearchBar,
  TableActionWrapper,
  TableFooterActionWrapper,
} from './InternalDocumentIncomingDocPageStyled'
// Ant design
import {
  CheckOutlined,
  CloseCircleOutlined,
  FilterOutlined,
  PlusOutlined,
  PrinterOutlined,
} from '@ant-design/icons'
import {
  Button,
  Col,
  Empty,
  Input,
  message,
  Modal,
  Pagination,
  Row,
  Space,
} from 'antd'
//other
import { inject, observer } from 'mobx-react'
import moment from 'moment'
import queryString from 'query-string'
import { useLocation, withRouter } from 'react-router-dom'
import AccessControlAction from '../../../components/AccessControlAction'
import { systemName } from '../../../config'
import {
  ACL_ACTION_TYPE,
  DATE_FORMAT_DEFAULT,
  DOCUMENT_TYPE,
  TYPE_STATUS,
} from '../../../constants'
import utils from '../../../utils'

const { Search } = Input

const COMPLETED_STATUS_TEXT = 'Hoàn thành'
const WARNING_OVERDUE_TEXT = 'Quá hạn'
const WARNING_NOT_DUE_TEXT = 'Chưa đến hạn'

const getIncomingDocumentCompletionInfo = document => {
  const isComplete = document?.is_completed === true

  // Nếu có completed_at → so sánh chính xác
  // Nếu không (record cũ) → fallback moment() hiện tại
  const compareDate = document?.completed_at
    ? moment(document.completed_at)
    : moment()
  console.log('=== DEBUG COMPLETION ===')
  console.log('code:', document?.code)
  console.log('end_date raw:', document?.end_date)
  console.log('completed_at raw:', document?.completed_at)
  console.log(
    'end_date parsed:',
    moment(document?.end_date).format('YYYY-MM-DD HH:mm:ssZ')
  )
  console.log(
    'completed_at parsed:',
    compareDate.format('YYYY-MM-DD HH:mm:ssZ')
  )
  console.log('isOverdue:', compareDate.isAfter(moment(document?.end_date)))
  console.log('========================')

  const isOverdue =
    !!document?.end_date && compareDate.isAfter(moment(document.end_date))

  return {
    isComplete,
    isOverdue,
    completedStatusText: COMPLETED_STATUS_TEXT,
  }
}

const InternalDocumentIncomingDocPage = props => {
  const {
    history,
    internalDocumentStore,
    loadingAnimationStore,
    authorityStore,
    connectedDocumentStore,
  } = props

  const {
    internalDocumentIncomingList,
    internalDocumentIncomingListTotalCount,
    queryParamsIncoming,
  } = internalDocumentStore

  const location = useLocation()
  const queryStringParse = queryString.parse(location.search)

  const [showAdvanceFilter, setShowAdvanceFilter] = useState(false)
  const [countFilter, setCountFilter] = useState(0)
  const [exportLoading, setExportLoading] = useState(false)
  const [isRefreshPage, setIsRefreshPage] = useState(true)
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [markCompleteLoading, setMarkCompleteLoading] = useState(false)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [completionText, setCompletionText] = useState('')

  const handleMarkComplete = useCallback(() => {
    setCompletionText('')
    setIsModalVisible(true)
  }, [])

  const handleModalOk = useCallback(async () => {
    if (!completionText.trim()) {
      message.error('Vui lòng nhập thông tin bộ phận thực hiện')
      return
    }

    const completedAt = moment().toISOString() // lấy thời điểm bấm hoàn thành

    setMarkCompleteLoading(true)
    try {
      await Promise.all(
        selectedRowKeys.map(id => {
          const record = internalDocumentIncomingList.find(d => d.id === id)
          return record
            ? internalDocumentStore.updateInternalDocumentIsCompleted(
                record.code,
                true,
                completionText,
                completedAt // ← truyền thêm
              )
            : Promise.resolve()
        })
      )
      setSelectedRowKeys([])
      setIsModalVisible(false)
      setCompletionText('')
      message.success('Đánh dấu hoàn thành thành công')
      await internalDocumentStore.getInternalDocumentIncomingList()
    } catch (err) {
      message.error(err?.vi || 'Đã có lỗi xảy ra!')
    } finally {
      setMarkCompleteLoading(false)
    }
  }, [selectedRowKeys, internalDocumentIncomingList, completionText])

  const handleModalCancel = useCallback(() => {
    setIsModalVisible(false)
    setCompletionText('')
  }, [])

  const NGAY_DEN_WIDTH = 100
  const NGAY_VAN_BAN_WIDTH = 100
  const SO_HIEU_WIDTH = 120
  const TRANG_THAI_WIDTH = 120
  const TRICH_YEU_WIDTH = 220
  const CO_QUAN_BAN_HANH_WIDTH = 200
  const BO_PHAN_THUC_HIEN_WIDTH = 180
  const NGAY_HOAN_THANH_WIDTH = 110
  const SCROLL_WIDTH = 1260
  const SO_DEN_WIDTH = 60

  const tableColumns = useMemo(
    () => [
      {
        title: 'Số đến',
        width: SO_DEN_WIDTH,
        render: record => (
          <UnreadText
            text={record.incoming_number}
            isUnread={checkUnread(record)}
            width={SO_DEN_WIDTH}
          />
        ),
      },
      {
        title: 'Số hiệu',
        width: SO_HIEU_WIDTH,
        render: record => (
          <UnreadText
            text={record.document_number}
            isUnread={checkUnread(record)}
            width={SO_HIEU_WIDTH}
          />
        ),
      },
      {
        title: 'Ngày đến',
        width: NGAY_DEN_WIDTH,
        render: record => (
          <UnreadText
            text={moment(record.incoming_date).format(DATE_FORMAT_DEFAULT)}
            isUnread={checkUnread(record)}
            width={NGAY_DEN_WIDTH}
          />
        ),
      },
      {
        title: 'Ngày văn bản',
        width: NGAY_VAN_BAN_WIDTH,
        render: record =>
          record.date_issued ? (
            <UnreadText
              text={moment(record.date_issued).format(DATE_FORMAT_DEFAULT)}
              isUnread={checkUnread(record)}
              width={NGAY_VAN_BAN_WIDTH}
            />
          ) : (
            <TextWrap>
              <EmptyText>-------------------------</EmptyText>
            </TextWrap>
          ),
      },
      {
        align: 'center',
        title: 'Trạng thái',
        width: TRANG_THAI_WIDTH,
        render: record => (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
            }}>
            {StatusTag(
              TYPE_STATUS.INTERNAL_DOCUMENT_INCOMING,
              record.document_status
            )}
          </div>
        ),
      },
      {
        title: 'Trích yếu',
        width: TRICH_YEU_WIDTH,
        render: record => (
          <UnreadText
            text={record.title}
            isUnread={checkUnread(record)}
            width={TRICH_YEU_WIDTH}
          />
        ),
      },
      {
        title: 'Cơ quan ban hành',
        width: CO_QUAN_BAN_HANH_WIDTH,
        render: record => (
          <UnreadText
            text={renderAuthorityIssuedName(record)}
            isUnread={checkUnread(record)}
            width={CO_QUAN_BAN_HANH_WIDTH}
          />
        ),
      },
      {
        title: 'Hạn hoàn thành',
        width: NGAY_HOAN_THANH_WIDTH,
        render: record =>
          record.end_date ? (
            <UnreadText
              text={moment(record.end_date).format('DD/MM/YYYY HH:mm')}
              isUnread={checkUnread(record)}
              width={NGAY_HOAN_THANH_WIDTH}
            />
          ) : (
            <TextWrap>
              <EmptyText>-</EmptyText>
            </TextWrap>
          ),
      },
      {
        title: 'Cảnh báo',
        width: 320,
        render: record => {
          const {
            isComplete,
            isOverdue,
            completedStatusText,
          } = getIncomingDocumentCompletionInfo(record)

          if (isComplete)
            return (
              <div
                style={{
                  color: 'green',
                  whiteSpace: 'nowrap',
                }}>
                {completedStatusText}
              </div>
            )

          const warningText = record.end_date
            ? moment().isAfter(moment(record.end_date)) // ← bỏ 'day'
              ? WARNING_OVERDUE_TEXT
              : WARNING_NOT_DUE_TEXT
            : null
          if (!warningText)
            return (
              <TextWrap>
                <EmptyText>-</EmptyText>
              </TextWrap>
            )
          if (warningText === WARNING_NOT_DUE_TEXT)
            return (
              <span
                style={{
                  borderRadius: 10,
                  margin: 0,
                  color: 'orange',
                  whiteSpace: 'nowrap',
                  display: 'inline-block',
                }}>
                {warningText}
              </span>
            )
          return (
            <span
              style={{
                color: '#ff4d4f',
                fontWeight: 500,
                fontSize: 12,
                whiteSpace: 'nowrap',
                display: 'inline-block',
              }}>
              {warningText}
            </span>
          )
        },
      },
      {
        title: 'Bộ phận thực hiện',
        width: BO_PHAN_THUC_HIEN_WIDTH,
        render: record =>
          record.handler_department ? (
            <UnreadText
              text={record.handler_department}
              isUnread={checkUnread(record)}
              width={BO_PHAN_THUC_HIEN_WIDTH}
            />
          ) : (
            <TextWrap>
              <EmptyText>-</EmptyText>
            </TextWrap>
          ),
      },
    ],
    []
  )

  const checkUnread = record => record.status_internal_incoming !== 'READ'

  const renderAuthorityIssuedName = (name, isText = false) => {
    if (name.authority_issued_name) {
      return name.authority_issued_name
    }
    if (name.other_authority_issued) {
      return (
        (isText && name.other_authority_issued) || (
          <span>{name.other_authority_issued}</span>
        )
      )
    }
    return ''
  }

  const exportDocument = useCallback(async () => {
    setExportLoading(true)

    const col = tableColumns.map(col => col.title)
    let rows = []
    rows.push(col)
    try {
      const res = await internalDocumentStore.getForExportInternalDocumentIncomingListAll()
      if (res && res.data && res.data.data) {
        res.data.data.map(item => {
          const {
            isComplete,
            completedStatusText,
          } = getIncomingDocumentCompletionInfo(item)

          const statusText = StatusTag(
            TYPE_STATUS.INTERNAL_DOCUMENT_INCOMING,
            item.document_status,
            true
          )

          const warningText = isComplete
            ? completedStatusText
            : item.end_date
            ? moment().isAfter(moment(item.end_date)) // ← bỏ 'day'
              ? WARNING_OVERDUE_TEXT
              : WARNING_NOT_DUE_TEXT
            : '-'
          rows.push([
            item.incoming_number,
            item.document_number,
            moment(item.incoming_date).format(DATE_FORMAT_DEFAULT),
            (item.date_issued &&
              moment(item.date_issued).format(DATE_FORMAT_DEFAULT)) ||
              '-------------------------',
            statusText,
            item.title,
            renderAuthorityIssuedName(item, true),
            (item.end_date &&
              moment(item.end_date).format(DATE_FORMAT_DEFAULT)) ||
              '-',
            warningText,
            item.handler_department || '-',
          ])
        })
      }
      utils.exportExcel(
        rows,
        'VBNB - văn bản đến - ' + moment().format('YYYY-MM-DD hmms')
      )
      internalDocumentStore.setQueryParamsIncoming({
        ...queryParamsIncoming,
        ...queryStringParse,
      })
    } catch (error) {
      console.log(error)
      message.error(error.vi || 'Đã có lỗi xảy ra!')
    } finally {
      setExportLoading(false)
    }
  }, [queryParamsIncoming])
  const handleQuickSearch = title => {
    internalDocumentStore.setQueryParamsIncoming({
      ...queryParamsIncoming,
      page: 0,
      title: title && title.trim(),
    })
    const queryStringStringify = queryString.stringify(
      {
        ...queryStringParse,
        page: 0,
        size: 10,
        title: title ? title.trim() : null,
      },
      {
        skipNull: true,
      }
    )
    history.replace(
      `/internal-document/incoming-document?${queryStringStringify}`
    )
  }

  const onChangePagination = pageIndex => {
    internalDocumentStore.setQueryParamsIncoming({
      ...queryParamsIncoming,
      page: pageIndex - 1,
      size: 10,
    })
    const queryStringStringify = queryString.stringify(
      {
        ...queryStringParse,
        page: pageIndex - 1,
        size: 10,
      },
      {
        skipNull: true,
      }
    )
    history.replace(
      `/internal-document/incoming-document?${queryStringStringify}`
    )
  }

  const getInternalDocumentIncomingList = async () => {
    try {
      loadingAnimationStore.setTableLoading(true)
      await internalDocumentStore.getInternalDocumentIncomingList()
    } catch (error) {
      console.log(error)
      message.error(error?.vi || 'Đã có lỗi xảy ra!')
    } finally {
      loadingAnimationStore.setTableLoading(false)
    }
  }

  useEffect(() => {
    internalDocumentStore.setQueryParamsIncoming({
      ...queryParamsIncoming,
      ...queryStringParse,
    })
    ;(async () => {
      try {
        loadingAnimationStore.setTableLoading(true)
        authorityStore.setAuthorityIssuedsQueryParams({
          type: DOCUMENT_TYPE.INCOMING,
          key: null,
        })
        await Promise.all([
          authorityStore.getAuthorityIssueds(),
          internalDocumentStore.getInternalDocumentIncomingList(),
        ])
        setIsRefreshPage(false)
      } catch (err) {
        console.log(err)
        message.success(err.vi || 'Đã có lỗi xảy ra!')
      } finally {
        loadingAnimationStore.setTableLoading(false)
      }
    })()
    return () => {
      internalDocumentStore.clearInternalDocumentIncomingList()
      internalDocumentStore.clearQueryParamsIncoming()
    }
  }, [])

  useEffect(() => {
    if (isRefreshPage) return
    ;(async () => {
      await getInternalDocumentIncomingList()
    })()
  }, [queryParamsIncoming])

  useEffect(() => {
    let count = 0
    if (queryStringParse.title) {
      count++
    }
    if (queryStringParse.document_number) {
      count++
    }
    if (queryStringParse.from_date_issued && queryStringParse.to_date_issued) {
      count++
    }
    if (
      queryStringParse.from_incoming_date &&
      queryStringParse.to_incoming_date
    ) {
      count++
    }
    if (queryStringParse.signer) {
      count++
    }
    if (queryStringParse.authority_name) {
      count++
    }
    setCountFilter(count)
  }, [location])

  return (
    <DocumentsLayout
      title={
        <PageTitle location={props.location} title="Văn bản đến" hiddenGoBack>
          <AccessControlAction aclActionType={ACL_ACTION_TYPE.create__INCOMING}>
            <Button
              icon={<PlusOutlined />}
              onClick={() => {
                connectedDocumentStore.clearDataReceiveDocument()
                history.push('/internal-document/incoming-document/create')
              }}
              type={'primary'}>
              Tạo mới văn bản đến
            </Button>
          </AccessControlAction>
        </PageTitle>
      }
      selectedKey="incoming-document"
      linkToIncomingDocument="/internal-document/incoming-document"
      linkToOutgoingDocument="/internal-document/outgoing-document"
      linkToSignDocument="/internal-document/sign-document"
      linkToConfigDocument="/internal-document/config-document/document-books">
      <Helmet>
        <title>Văn bản đến | {systemName} Quản lý VB VIMC</title>
      </Helmet>
      <TableActionWrapper>
        <Row>
          <Col span={12}>
            {!showAdvanceFilter ? (
              <SearchBar>
                <Search
                  allowClear
                  onSearch={value => handleQuickSearch(value)}
                  defaultValue={queryStringParse.title}
                  placeholder={
                    'Tìm kiếm văn bản theo Trích yếu / Số hiệu / Số đến'
                  }
                />
              </SearchBar>
            ) : null}
          </Col>
          <Col span={12} style={{ textAlign: 'right' }}>
            <FilterButtonWrapper>
              <span
                className={'count-filter'}
                style={{ display: !countFilter && 'none' }}>
                {countFilter}
              </span>
              <Button
                type={showAdvanceFilter ? 'primary' : 'default'}
                ghost={showAdvanceFilter}
                style={{ marginBottom: showAdvanceFilter ? 10 : 0 }}
                onClick={() => setShowAdvanceFilter(!showAdvanceFilter)}>
                {showAdvanceFilter ? (
                  <Space>
                    <CloseCircleOutlined />
                    Ẩn tìm kiếm nâng cao
                  </Space>
                ) : (
                  <Space>
                    <FilterOutlined />
                    Tìm kiếm nâng cao
                  </Space>
                )}
              </Button>
            </FilterButtonWrapper>
            <Button
              loading={markCompleteLoading}
              onClick={handleMarkComplete}
              disabled={selectedRowKeys.length === 0}
              style={{ marginLeft: '10px' }}>
              <CheckOutlined />
              Đánh dấu đã hoàn thành
            </Button>
            <Button
              loading={exportLoading}
              onClick={exportDocument}
              style={{ marginLeft: '10px' }}>
              <PrinterOutlined />
              In
            </Button>
          </Col>
        </Row>
        {showAdvanceFilter ? <AdvanceFilterBlock /> : null}
        <TableComponent
          onRow={record => {
            return {
              onClick: () =>
                history.push(
                  `/internal-document/incoming-document/view/${record.code}`
                ),
            }
          }}
          rowKey={record => record.id}
          dataSource={internalDocumentIncomingList}
          columns={tableColumns}
          pagination={false}
          scroll={{ x: SCROLL_WIDTH }}
          rowSelection={{
            selectedRowKeys,
            onChange: keys => setSelectedRowKeys(keys),
            getCheckboxProps: record => ({
              disabled: record.is_completed === true,
            }),
          }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={'Không có dữ liệu'}
              />
            ),
          }}
          loading={{
            tip: 'Đang tải văn bản...',
            spinning: loadingAnimationStore.tableLoading,
          }}
        />
        <TableFooterActionWrapper>
          <Pagination
            onChange={e => onChangePagination(e)}
            hideOnSinglePage={true}
            total={internalDocumentIncomingListTotalCount}
            pageSize={queryParamsIncoming.size}
            current={queryParamsIncoming.page + 1}
            showSizeChanger={false}
            showLessItems
          />
        </TableFooterActionWrapper>
      </TableActionWrapper>

      <Modal
        title="Hoàn thành văn bản"
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        confirmLoading={markCompleteLoading}
        okText="Lưu"
        cancelText="Hủy"
        width={500}>
        <div style={{ marginBottom: '16px' }}>
          <label
            style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '500',
            }}>
            Bộ phận thực hiện <span style={{ color: 'red' }}>*</span>
          </label>
          <Input
            placeholder="Nhập thông tin bộ phận thực hiện"
            value={completionText}
            onChange={e => setCompletionText(e.target.value)}
          />
        </div>
      </Modal>
    </DocumentsLayout>
  )
}

InternalDocumentIncomingDocPage.propTypes = {}

export default memo(
  withRouter(
    inject(
      'internalDocumentStore',
      'loadingAnimationStore',
      'authorityStore',
      'connectedDocumentStore',
      'selectUserStore'
    )(observer(InternalDocumentIncomingDocPage))
  )
)
